// df-9548d1ca90e9 — the one rule for "we already did this for this person once,
// is it too soon to do it again?"
//
// Several unrelated places ask that question: the in-app "Update available"
// card, the daily conflict scan, the checkpoint-integrity check, and the review
// prompt's once-a-year cap and its quiet period after somebody reports a
// problem. Before this module each of them wrote its own version, and each had
// to remember the same trap on its own.
//
// **The trap.** Luxon answers an unreadable date with an INVALID `DateTime`
// rather than by throwing, and every comparison against an invalid one is
// false. So the natural way to write the check — compare the stored moment
// against a cut-off — silently switches the waiting period OFF when the stored
// value is damaged, and nothing reports it. #4625 had to guard that in four
// separate places inside one feature.
//
// **Why the unreadable answer is an argument and not a default.** The places
// asking do not want the same answer when the stored moment cannot be read, and
// picking one for them would be wrong for the others:
//
//   - the review prompt must HOLD BACK. Its marker rides a synced document, and
//     a passing unreadable value switches the once-a-year cap off and asks
//     somebody again and again.
//   - the update card, the conflict scan and the checkpoint check must CARRY
//     ON. Showing the card a day early is harmless; the conflict scan's own
//     comment says carrying on is what stops every upgrading device re-scanning
//     at once; and holding back the integrity check would suppress a health
//     check.
//
// So `whenUnreadable` has no default. A caller cannot get an answer to that
// question by not thinking about it.
//
// **How the window is compared, and why it is spelled this way.** The test is
// `lastHappened.plus(window) <= now`, which is exactly equivalent to what all
// four call sites did before — including the review prompt's 365-day window,
// where Luxon's `diff(then, 'days').days` is calendar-aware and so is
// `plus({ days: 365 })`. Writing it as an elapsed-milliseconds comparison would
// NOT be equivalent: across a daylight-saving change a calendar year and 365
// exact days differ by an hour.
//
// Angular-free and dependency-free on purpose, so the rule can be driven
// directly by a test. See `periodic-suppression.test.ts`.

import { DateTime, DurationLikeObject } from 'luxon';

/**
 * What the answer should be when the stored moment cannot be read at all.
 *
 * There is no default and no "sensible" choice — see this file's header. Pick
 * by asking what the cost of being wrong is: `Suppress` when doing the thing
 * again would pester somebody or lose a cap, `Allow` when doing it again is
 * cheap and skipping it would suppress something that matters.
 */
export enum WhenMomentUnreadable {
  /** A moment we cannot read keeps the thing SUPPRESSED. */
  Suppress = 'suppress',
  /** A moment we cannot read lets the thing HAPPEN. */
  Allow = 'allow',
}

/**
 * Read a stored moment, or `undefined` when there is not a usable one.
 *
 * Absent, blank and unparseable all come back the same way. That is deliberate:
 * these values round-trip through JSON documents and browser storage, so a
 * hand-edited or half-written string really can turn up, and the three cases
 * are not separately actionable to a reader.
 *
 * A caller that must tell "never happened" apart from "happened, but the record
 * is damaged" does not use this — it uses {@link mayHappenAgainSinceStored},
 * which keeps those two apart because they get different answers.
 */
export function readStoredMoment(stored: string | undefined | null): DateTime | undefined {
  // if there is nothing usable to parse, say so rather than handing back an invalid DateTime
  if (!stored || typeof stored !== 'string' || stored.trim() === '') return undefined;
  const parsed = DateTime.fromISO(stored);
  return parsed.isValid ? parsed : undefined;
}

/**
 * May this happen again yet, given when it last happened?
 *
 * `lastHappened` being `undefined` or `null` means it has NEVER happened, which
 * always answers yes. An INVALID `DateTime` is the different case — it happened
 * and the record is damaged — and that is what `whenUnreadable` decides.
 *
 * An unusable `now` is treated the same as an unreadable stored moment. No
 * caller can currently reach that (each passes the system clock, and the review
 * gate refuses on an unusable clock long before it gets here), but answering it
 * silently with `false` would be the quiet-failure direction this module exists
 * to remove.
 */
export function mayHappenAgain(
  lastHappened: DateTime | undefined | null,
  window: DurationLikeObject,
  now: DateTime,
  whenUnreadable: WhenMomentUnreadable,
): boolean {
  // if it has never happened, there is nothing to wait for
  if (lastHappened === undefined || lastHappened === null) return true;
  // if either end of the comparison is unusable, the caller's own choice decides
  if (!lastHappened.isValid || !now || !now.isValid) {
    return whenUnreadable === WhenMomentUnreadable.Allow;
  }
  return lastHappened.plus(window) <= now;
}

/**
 * The same question, asked straight off a stored string.
 *
 * Absent and blank mean it has never happened, so they answer yes whatever
 * `whenUnreadable` says. Only a string that is there and cannot be parsed is
 * "unreadable" — that distinction is the whole reason this does not simply call
 * {@link readStoredMoment} and pass the result on.
 */
export function mayHappenAgainSinceStored(
  storedMoment: string | undefined | null,
  window: DurationLikeObject,
  now: DateTime,
  whenUnreadable: WhenMomentUnreadable,
): boolean {
  // if nothing was ever stored, it has never happened
  if (!storedMoment || typeof storedMoment !== 'string' || storedMoment.trim() === '') {
    return true;
  }
  // a stored string that will not parse is the unreadable case, not the never case
  return mayHappenAgain(DateTime.fromISO(storedMoment), window, now, whenUnreadable);
}

/**
 * The later of two stored moments — the merge rule for a marker that SYNCS.
 *
 * A marker kept on a synced, last-writer-wins document needs merging rather
 * than assigning: a device that has not synced yet would otherwise write an
 * older value straight over a newer one, and the record saying "we already did
 * this" simply disappears with nothing reporting it.
 *
 * Returns the winner's ORIGINAL string rather than a re-serialized one, so a
 * merge that changes nothing really writes nothing back and cannot start a
 * write loop between two devices.
 *
 * An unusable value loses to a usable one, whichever side it is on. It does NOT
 * win by being "different": the point of this function is that a device with
 * nothing stored, or with something broken stored, can never wipe out a real
 * date held by the other side.
 */
export function mergeStoredMoments(
  local: string | undefined,
  remote: string | undefined,
): string {
  const localTime = readStoredMoment(local);
  const remoteTime = readStoredMoment(remote);

  if (localTime === undefined && remoteTime === undefined) return '';
  if (localTime === undefined) return remote;
  if (remoteTime === undefined) return local;
  return remoteTime > localTime ? remote : local;
}
